import { useCallback } from "react";

import { styled } from "@linaria/react";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import type { LoggingProperties } from "@gx/logging";
import { LoggingId, posthogEvent } from "@gx/logging";

import { GalaxyTheme } from "../theme";
import { useGalaxyTheme, withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import FlexItem from "../containers/FlexItem";
import Icon, { IconVariant } from "../icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "../text/Text";

export interface ButtonProps {
  label?: string;
  icon?: PhosphorIcon;
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  isIconTrailing?: boolean;
  fillWidth?: boolean;
  isEllipsis?: boolean;
  cursor?: React.CSSProperties["cursor"];
  loggingId?: LoggingId; // TODO: Remove this prop once all buttons have a loggingId
  loggingProperties?: LoggingProperties;
}

export enum ButtonVariant {
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

export enum ButtonSize {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

const StyledButton = withTheme(styled.button<
  PropsWithTheme<{
    $variant: ButtonVariant;
    $size: ButtonSize;
    $fillWidth?: boolean;
    $noLabel?: boolean;
    $isEllipsis?: boolean;
    $cursor?: React.CSSProperties["cursor"];
  }>
>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "auto")};
  min-width: ${({ $isEllipsis }) => ($isEllipsis ? "0" : "auto")};
  overflow: ${({ $isEllipsis }) => ($isEllipsis ? "hidden" : "visible")};

  gap: ${({ $size }) => {
    return match($size)
      .with(ButtonSize.SMALL, () => "4px")
      .with(ButtonSize.MEDIUM, () => "6px")
      .with(ButtonSize.LARGE, () => "6px")
      .exhaustive();
  }};

  padding: ${({ $size, $noLabel }) => {
    return match($size)
      .with(ButtonSize.SMALL, () => ($noLabel ? "0 6px" : "0 6px"))
      .with(ButtonSize.MEDIUM, () => ($noLabel ? "0 6px" : "0 8px"))
      .with(ButtonSize.LARGE, () => ($noLabel ? "0 8px" : "0 12px"))
      .exhaustive();
  }};

  height: ${({ $size }) => {
    return match($size)
      .with(ButtonSize.SMALL, () => "24px")
      .with(ButtonSize.MEDIUM, () => "28px")
      .with(ButtonSize.LARGE, () => "32px")
      .exhaustive();
  }};

  background-color: ${({ theme, $variant }) => {
    return match($variant)
      .with(ButtonVariant.PRIMARY, () => theme.color.background.galaxy)
      .with(ButtonVariant.SECONDARY, () => theme.color.background.primary)
      .with(ButtonVariant.TERTIARY, () => "transparent")
      .with(ButtonVariant.SUCCESS, () => theme.color.icon.success)
      .with(ButtonVariant.ERROR, () => theme.color.icon.error)
      .exhaustive();
  }};
  border: ${({ theme, $variant }) => {
    return match($variant)
      .with(ButtonVariant.PRIMARY, () => "0.5px solid transparent")
      .with(ButtonVariant.SECONDARY, () => `0.5px solid ${theme.color.border.primary}`)
      .with(ButtonVariant.TERTIARY, () => "0.5px solid transparent")
      .with(ButtonVariant.SUCCESS, () => "0.5px solid transparent")
      .with(ButtonVariant.ERROR, () => "0.5px solid transparent")
      .exhaustive();
  }};
  border-radius: 5px;

  cursor: ${({ $cursor }) => $cursor ?? "pointer"};

  transition:
    background-color 0.075s ease-in-out,
    border 0.075s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${({ theme, $variant }) => {
      return match($variant)
        .with(ButtonVariant.PRIMARY, () => theme.color.background.galaxyAlt)
        .with(ButtonVariant.SECONDARY, () => theme.color.background.tertiary)
        .with(ButtonVariant.TERTIARY, () => theme.color.background.secondary)
        .with(ButtonVariant.SUCCESS, () => theme.color.background.successAlt)
        .with(ButtonVariant.ERROR, () => theme.color.background.errorAlt)
        .exhaustive();
    }};

    border-color: ${({ theme, $variant }) => {
      return match($variant)
        .with(ButtonVariant.PRIMARY, () => theme.color.border.primary)
        .with(ButtonVariant.SECONDARY, () => theme.color.border.primary)
        .with(ButtonVariant.TERTIARY, () => theme.color.border.primary)
        .with(ButtonVariant.SUCCESS, () => "transparent")
        .with(ButtonVariant.ERROR, () => "transparent")
        .exhaustive();
    }};
  }

  &:disabled {
    background-color: ${({ theme, $variant }) => {
      return match($variant)
        .with(ButtonVariant.PRIMARY, () => theme.color.background.disabled)
        .with(ButtonVariant.SECONDARY, () => theme.color.background.disabled)
        .with(ButtonVariant.TERTIARY, () => "transparent")
        .with(ButtonVariant.SUCCESS, () => theme.color.background.successAlt)
        .with(ButtonVariant.ERROR, () => theme.color.background.errorAlt)
        .exhaustive();
    }};

    cursor: not-allowed;
    opacity: 0.5;
  }
`);

const LabelWrapper = styled.span<{ $isEllipsis?: boolean }>`
  flex: ${({ $isEllipsis }) => ($isEllipsis ? "1" : "0 1 auto")};
  min-width: ${({ $isEllipsis }) => ($isEllipsis ? "0" : "auto")};
  overflow: hidden;
`;

function getTextVariant(variant: ButtonVariant, galaxyTheme: GalaxyTheme) {
  return match(variant)
    .with(ButtonVariant.PRIMARY, () =>
      galaxyTheme === GalaxyTheme.DARK ? TextVariant.PRIMARY : TextVariant.PRIMARY_ALT,
    )
    .with(ButtonVariant.SECONDARY, () => TextVariant.PRIMARY)
    .with(ButtonVariant.TERTIARY, () => TextVariant.PRIMARY)
    .with(ButtonVariant.SUCCESS, () => TextVariant.PRIMARY_ALT)
    .with(ButtonVariant.ERROR, () => TextVariant.PRIMARY_ALT)
    .exhaustive();
}

function getIconVariant(variant: ButtonVariant, galaxyTheme: GalaxyTheme) {
  return match(variant)
    .with(ButtonVariant.PRIMARY, () =>
      galaxyTheme === GalaxyTheme.DARK ? IconVariant.PRIMARY : IconVariant.PRIMARY_ALT,
    )
    .with(ButtonVariant.SECONDARY, () => IconVariant.PRIMARY)
    .with(ButtonVariant.TERTIARY, () => IconVariant.PRIMARY)
    .with(ButtonVariant.SUCCESS, () => IconVariant.PRIMARY_ALT)
    .with(ButtonVariant.ERROR, () => IconVariant.PRIMARY_ALT)
    .exhaustive();
}

const BUTTON_SIZE_TO_TEXT_SIZE_MAP: Record<ButtonSize, TextSize> = {
  [ButtonSize.SMALL]: TextSize.BODY_SM,
  [ButtonSize.MEDIUM]: TextSize.BODY_MD,
  [ButtonSize.LARGE]: TextSize.BODY_LG,
};

const BUTTON_SIZE_TO_ICON_SIZE_MAP: Record<ButtonSize, number> = {
  [ButtonSize.SMALL]: 12,
  [ButtonSize.MEDIUM]: 14,
  [ButtonSize.LARGE]: 16,
};

const Button = ({
  label,
  icon,
  variant = ButtonVariant.PRIMARY,
  size = ButtonSize.MEDIUM,
  onClick,
  onMouseEnter,
  onMouseLeave,
  fillWidth,
  isLoading,
  isDisabled,
  isIconTrailing,
  isEllipsis,
  cursor,
  loggingId,
  loggingProperties,
}: ButtonProps) => {
  const { galaxyTheme } = useGalaxyTheme();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled || isLoading) {
        return;
      }

      onClick(e);

      // TODO: Remove this if statement once all buttons have a loggingId
      if (loggingId) {
        posthogEvent(loggingId, loggingProperties);
      }
    },
    [isDisabled, isLoading, onClick, loggingId, loggingProperties],
  );

  const renderIcon = useCallback(() => {
    if (!icon) {
      return null;
    }

    let iconVariant: IconVariant = getIconVariant(variant, galaxyTheme);
    if (isDisabled) {
      iconVariant = IconVariant.TERTIARY;
    }

    return (
      <FlexItem shrink={0} grow={0}>
        <Icon component={icon} variant={iconVariant} size={BUTTON_SIZE_TO_ICON_SIZE_MAP[size]} />
      </FlexItem>
    );
  }, [icon, variant, size, isDisabled, galaxyTheme]);

  const renderLabel = useCallback(() => {
    if (!label) {
      return null;
    }

    let textVariant: TextVariant = getTextVariant(variant, galaxyTheme);
    if (isDisabled) {
      textVariant = TextVariant.TERTIARY_ALT;
    }

    return (
      <LabelWrapper $isEllipsis={isEllipsis}>
        <Text
          variant={textVariant}
          weight={TextWeight.MEDIUM}
          size={BUTTON_SIZE_TO_TEXT_SIZE_MAP[size]}
          cursor={isDisabled ? "not-allowed" : cursor}
          isEllipsis={isEllipsis}
        >
          {label}
        </Text>
      </LabelWrapper>
    );
  }, [label, variant, size, isDisabled, isEllipsis, cursor, galaxyTheme]);

  return (
    <StyledButton
      $variant={variant}
      $size={size}
      $fillWidth={fillWidth}
      $noLabel={!label}
      $isEllipsis={isEllipsis}
      $cursor={cursor}
      onClick={handleClick}
      disabled={isDisabled || isLoading}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {!isIconTrailing && renderIcon()}
      {label && renderLabel()}
      {isIconTrailing && renderIcon()}
    </StyledButton>
  );
};

export default Button;
